import {
  type ReactNode,
  type ReactElement,
  forwardRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Body text variants using class-variance-authority
 */
const bodyTextVariants = cva('', {
  variants: {
    variant: {
      default: '',
      muted: 'text-muted-foreground',
    },
    size: {
      sm: 'text-sm',
      xs: 'text-xs',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
});

export interface BodyTextProps
  extends ComponentPropsWithoutRef<'p'>, VariantProps<typeof bodyTextVariants> {
  /**
   * The text variant (color/style)
   * @default 'default'
   */
  variant?: 'default' | 'muted';

  /**
   * The text size
   * @default 'sm'
   */
  size?: 'xs' | 'sm';

  /**
   * The semantic HTML element to render (p, span, div)
   * @default 'p'
   */
  as?: 'p' | 'span' | 'div';

  /**
   * The content to display
   */
  children: ReactNode;
}

/**
 * Base BodyText component with variant support
 */
const BodyText = forwardRef<HTMLParagraphElement, BodyTextProps>(
  (
    {
      variant = 'default',
      size = 'sm',
      as = 'p',
      children,
      className,
      ...props
    },
    ref
  ): ReactElement => {
    const Component = as;

    return (
      <Component
        ref={ref}
        className={cn(bodyTextVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

BodyText.displayName = 'BodyText';

/**
 * Body - Regular body text
 * @example
 * ```tsx
 * <Body>Regular body text content</Body>
 * ```
 */
export const Body = forwardRef<
  HTMLParagraphElement,
  Omit<BodyTextProps, 'variant' | 'size'>
>(({ as = 'p', ...props }, ref) => (
  <BodyText ref={ref} variant="default" size="sm" as={as} {...props} />
));
Body.displayName = 'Body';

/**
 * BodySmall - Small body text
 * @example
 * ```tsx
 * <BodySmall>Small text content</BodySmall>
 * ```
 */
export const BodySmall = forwardRef<
  HTMLParagraphElement,
  Omit<BodyTextProps, 'variant' | 'size'>
>(({ as = 'p', ...props }, ref) => (
  <BodyText ref={ref} variant="default" size="xs" as={as} {...props} />
));
BodySmall.displayName = 'BodySmall';

/**
 * BodyMuted - Muted body text
 * @example
 * ```tsx
 * <BodyMuted>Muted description text</BodyMuted>
 * ```
 */
export const BodyMuted = forwardRef<
  HTMLParagraphElement,
  Omit<BodyTextProps, 'variant' | 'size'>
>(({ as = 'p', ...props }, ref) => (
  <BodyText ref={ref} variant="muted" size="sm" as={as} {...props} />
));
BodyMuted.displayName = 'BodyMuted';

/**
 * BodySmallMuted - Small muted text
 * @example
 * ```tsx
 * <BodySmallMuted>Small muted text</BodySmallMuted>
 * ```
 */
export const BodySmallMuted = forwardRef<
  HTMLParagraphElement,
  Omit<BodyTextProps, 'variant' | 'size'>
>(({ as = 'p', ...props }, ref) => (
  <BodyText ref={ref} variant="muted" size="xs" as={as} {...props} />
));
BodySmallMuted.displayName = 'BodySmallMuted';
